/**
 * useMultiVendorCapabilities(isOpen, cartItems, cartTotal)
 *
 * Fetches fiat payment capabilities for ALL sellers in the cart.
 * Used by CartCheckoutModal to decide which payment options are available.
 *
 * Logic:
 *   - Intersection stricte : Stripe/PayPal enabled only if ALL sellers support it.
 *   - Cap fiat administrable (P1-1) : fiat disabled if total > cap serveur
 *     (GET /data/fiat-cap, fallback FIAT_CAP_USD=2000 via fetchFiatCapUsd).
 *   - Cancellation-safe: cleanup prevents state update on unmounted component.
 *
 * Returns: { stripeAvailable: bool, paypalAvailable: bool, loading: bool }
 *
 * For mono-vendor (BuyFlow), use useVendorCapabilities instead.
 */
import { useState, useEffect } from 'react';
import { getVendorCapabilities } from '../services/SellerPayoutsService';
import { fetchFiatCapUsd } from './useFiatCap';

export function useMultiVendorCapabilities(isOpen, cartItems, cartTotal) {
  const [stripeAvailable, setStripeAvailable] = useState(false);
  const [paypalAvailable, setPaypalAvailable] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOpen || !cartItems || cartItems.length === 0) {
      setStripeAvailable(false);
      setPaypalAvailable(false);
      setLoading(false);
      return;
    }

    const uniqueSellerIds = [...new Set(
      cartItems.map((i) => i.sellerId).filter(Boolean)
    )];

    if (uniqueSellerIds.length === 0) {
      setStripeAvailable(false);
      setPaypalAvailable(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetchFiatCapUsd(), // cap serveur (fallback FIAT_CAP_USD, jamais de throw)
      ...uniqueSellerIds.map((id) =>
        getVendorCapabilities(id).catch(() => ({ stripeEnabled: false, paypalEnabled: false }))
      ),
    ]).then(([fiatCapUsd, ...results]) => {
      if (cancelled) return;
      const allStripe = results.every((r) => !!r.stripeEnabled);
      const allPaypal = results.every((r) => !!r.paypalEnabled);
      const total = parseFloat(cartTotal) || 0;
      setStripeAvailable(allStripe && total <= fiatCapUsd);
      setPaypalAvailable(allPaypal && total <= fiatCapUsd);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  return { stripeAvailable, paypalAvailable, loading };
}
